/**
 * hintsStore.js — Mouse-following hint system state (#142).
 *
 * Owns:
 *   - intensity setting: 'off' | 'low' | 'high' (default 'high')
 *   - first-run onboarded flag
 *   - hover-intent show delay in ms (#296, default 300)
 *
 * Persistence: localStorage. Anonymous-tier behaviour is fine; logged-in
 * users get the same UX without an extra round-trip.
 */

import { createSignal } from 'solid-js';

const KEY_INTENSITY = 'v2.hints.intensity';
const KEY_ONBOARDED = 'v2.hints.onboarded';
const KEY_DELAY     = 'v2.hints.delayMs';

export const HINT_LEVELS = ['off', 'low', 'high'];

// #296 — hover-intent delay range (ms). 0 = legacy instant behaviour,
// 1000 = a full second of dwell required. Default 300 was chosen via
// the user-feedback wave that filed #296 ("too aggressive on incidental
// hover"); 250–400 is the sweet spot most users converge on.
export const HINT_DELAY_MIN = 0;
export const HINT_DELAY_MAX = 1000;
export const HINT_DELAY_DEFAULT = 300;

function loadIntensity() {
  try {
    const v = localStorage.getItem(KEY_INTENSITY);
    if (v && HINT_LEVELS.includes(v)) return v;
  } catch { /* private mode */ }
  return 'high';
}
function loadOnboarded() {
  try { return localStorage.getItem(KEY_ONBOARDED) === '1'; } catch { return false; }
}
function loadDelay() {
  try {
    const v = parseInt(localStorage.getItem(KEY_DELAY), 10);
    if (Number.isFinite(v) && v >= HINT_DELAY_MIN && v <= HINT_DELAY_MAX) return v;
  } catch { /* private mode */ }
  return HINT_DELAY_DEFAULT;
}

const [intensity, setIntensity] = createSignal(loadIntensity());
const [onboarded, setOnboarded] = createSignal(loadOnboarded());
const [delayMs,   setDelayMs]   = createSignal(loadDelay());

export const hints = {
  intensity,
  onboarded,
  delayMs,
  setIntensity(v) {
    if (!HINT_LEVELS.includes(v)) return;
    setIntensity(v);
    try { localStorage.setItem(KEY_INTENSITY, v); } catch { /* ignore */ }
  },
  setDelayMs(v) {
    const n = Math.max(HINT_DELAY_MIN, Math.min(HINT_DELAY_MAX, parseInt(v, 10) || 0));
    setDelayMs(n);
    try { localStorage.setItem(KEY_DELAY, String(n)); } catch { /* ignore */ }
  },
  markOnboarded() {
    setOnboarded(true);
    try { localStorage.setItem(KEY_ONBOARDED, '1'); } catch { /* ignore */ }
  },
};

/**
 * Decide whether a given hint level should be shown.
 *   data-hint-level="critical" → show on Low+High
 *   data-hint-level="normal"   → show on High only (default)
 */
export function shouldShow(hintLevel) {
  const cur = intensity();
  if (cur === 'off') return false;
  if (cur === 'high') return true;
  // 'low' — only critical hints
  return hintLevel === 'critical';
}

export default hints;
